import React from 'react';
import { ShoppingCart, CheckCircle, RefreshCw, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const SuggestedOrderTab = ({
  suggestedOrder,
  suggestedLoading,
  suggestedViewMode,
  setSuggestedViewMode,
  suggestedSupplier,
  setSuggestedSupplier,
  loadSuggestedOrder
}) => {
  // Filter products by supplier
  const filteredProducts = suggestedSupplier === 'all' 
    ? (suggestedOrder?.products_to_order || [])
    : (suggestedOrder?.products_to_order || []).filter(p => p.supplier === suggestedSupplier);

  // Copy to WhatsApp
  const copyToWhatsApp = () => {
    if (!suggestedOrder?.products_to_order?.length) {
      toast.error('No hay productos para pedir');
      return;
    }
    
    const productsToOrder = filteredProducts;
    
    if (productsToOrder.length === 0) {
      toast.error('No hay productos para este proveedor');
      return;
    }
    
    let content = `*PEDIDO SUGERIDO*\n`;
    content += `Período: ${suggestedOrder.period.start_date} al ${suggestedOrder.period.end_date}\n\n`;
    
    // Group by supplier
    const bySupplier = {};
    productsToOrder.forEach(p => {
      const supplier = p.supplier || 'Sin proveedor';
      if (!bySupplier[supplier]) bySupplier[supplier] = [];
      bySupplier[supplier].push(p);
    });
    
    Object.entries(bySupplier).forEach(([supplier, products]) => {
      content += `*${supplier}*\n`;
      products.forEach(p => {
        if (suggestedViewMode === 'boxes' && p.cajas_sugeridas) {
          content += `• ${p.item_name}: *${p.cajas_sugeridas} ${p.unidad_compra || 'cajas'}*\n`;
        } else {
          content += `• ${p.item_name}: *${p.to_order} ${p.unit}*\n`;
        }
      });
      content += `\n`;
    });
    
    navigator.clipboard.writeText(content).then(() => {
      toast.success('Pedido copiado al portapapeles');
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  return (
    <div className="space-y-6" data-testid="suggested-order-tab">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-primary" />
              Pedido Sugerido
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Basado en demanda de hoy + últimos 8 días vs stock actual
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={suggestedViewMode} onValueChange={setSuggestedViewMode}>
              <SelectTrigger className="w-[140px]" data-testid="suggested-view-mode">
                <SelectValue placeholder="Vista" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="units">Unidades</SelectItem>
                <SelectItem value="boxes">Cajas</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadSuggestedOrder} disabled={suggestedLoading} variant="outline" data-testid="update-suggested-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${suggestedLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              onClick={copyToWhatsApp}
              className="bg-green-600 hover:bg-green-700"
              disabled={!suggestedOrder?.products_to_order?.length || filteredProducts.length === 0}
              data-testid="copy-suggested-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Copiar WhatsApp
            </Button>
          </div>
        </div>
        
        {suggestedLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : suggestedOrder ? (
          <div className="space-y-6">
            {/* Period info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Período analizado:</strong> {suggestedOrder.period.start_date} al {suggestedOrder.period.end_date} ({suggestedOrder.period.days} días)
              </p>
            </div>
            
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Productos con Demanda</p>
                <p className="text-2xl font-bold text-slate-900">{suggestedOrder.summary.total_products_with_demand}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Productos a Pedir</p>
                <p className="text-2xl font-bold text-orange-600">{suggestedOrder.summary.products_to_order}</p>
              </div>
              <div className="bg-primary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Total Unidades</p>
                <p className="text-2xl font-bold text-primary">{suggestedOrder.summary.total_units_to_order}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Total Cajas</p>
                <p className="text-2xl font-bold text-amber-600">{suggestedOrder.summary.total_boxes_to_order}</p>
              </div>
            </div>
            
            {/* Supplier filter */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Filtrar por Proveedor:</label>
              <Select value={suggestedSupplier} onValueChange={setSuggestedSupplier}>
                <SelectTrigger className="w-[250px]" data-testid="suggested-supplier-filter">
                  <SelectValue placeholder="Todos los proveedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proveedores</SelectItem>
                  {Object.keys(suggestedOrder.by_supplier || {}).map(supplier => (
                    <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Products table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Código</th>
                    <th className="px-4 py-3 text-left font-semibold">Producto</th>
                    <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                    <th className="px-4 py-3 text-right font-semibold">Demanda (9 días)</th>
                    <th className="px-4 py-3 text-right font-semibold bg-green-50">Stock Bodega</th>
                    <th className="px-4 py-3 text-right font-semibold bg-orange-50">A Pedir</th>
                    {suggestedViewMode === 'boxes' && (
                      <>
                        <th className="px-4 py-3 text-center font-semibold bg-amber-50">Un/Caja</th>
                        <th className="px-4 py-3 text-right font-semibold bg-amber-50">Cajas</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProducts.map((product, index) => (
                    <tr key={product.item_id} className={`hover:bg-slate-50 ${index % 2 === 0 ? '' : 'bg-slate-25'}`}>
                      <td className="px-4 py-3 font-mono text-sm">{product.item_code}</td>
                      <td className="px-4 py-3 font-medium">{product.item_name}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md">
                          {product.supplier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {product.demand_period} {product.unit}
                      </td>
                      <td className="px-4 py-3 text-right bg-green-50/50 text-green-700 font-medium">
                        {product.stock_bodega} {product.unit}
                      </td>
                      <td className="px-4 py-3 text-right bg-orange-50/50 text-orange-600 font-bold">
                        {product.to_order} {product.unit}
                      </td>
                      {suggestedViewMode === 'boxes' && (
                        <>
                          <td className="px-4 py-3 text-center bg-amber-50/50">
                            {product.unidades_por_caja || '-'}
                          </td>
                          <td className="px-4 py-3 text-right bg-amber-50/50 font-bold text-amber-700">
                            {product.cajas_sugeridas || '-'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={suggestedViewMode === 'boxes' ? 8 : 6} className="px-4 py-8 text-center text-slate-500">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        ¡Stock suficiente! No hay productos que pedir.
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredProducts.length > 0 && (
                  <tfoot className="bg-slate-100 font-bold">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right">TOTAL:</td>
                      <td className="px-4 py-3 text-right">-</td>
                      <td className="px-4 py-3 text-right bg-green-50/50">-</td>
                      <td className="px-4 py-3 text-right bg-orange-50/50 text-orange-600">
                        {filteredProducts.reduce((sum, p) => sum + p.to_order, 0)} unidades
                      </td>
                      {suggestedViewMode === 'boxes' && (
                        <>
                          <td className="px-4 py-3 text-center bg-amber-50/50">-</td>
                          <td className="px-4 py-3 text-right bg-amber-50/50 text-amber-700">
                            {filteredProducts.reduce((sum, p) => sum + (p.cajas_sugeridas || 0), 0)} cajas
                          </td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p>Haz clic en "Actualizar" para cargar el pedido sugerido</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestedOrderTab;
